import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Eye, EyeOff, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LogoMark } from "./LogoMark";
import { GoogleIcon } from "./GoogleIcon";
import { startLenis, stopLenis } from "@/lib/lenis";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const signInWithGoogle = () => {
  const redirectUrl = window.location.origin + window.location.pathname;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const inputCls =
  "w-full rounded-xl bg-white border border-sand pl-11 pr-4 py-3.5 text-sm text-coal placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-pine focus:ring-2 focus:ring-pine/15";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-sage";

const Field = ({ id, icon: Icon, label, children }) => (
  <label className="grid gap-2">
    <span className={labelCls}>{label}</span>
    <span className="relative">
      <Icon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-sage" />
      {children}
    </span>
  </label>
);

export default function AuthModal() {
  const { authModal, closeAuth, signInWithPassword, registerWithPassword } = useAuth();
  const [mode, setMode] = useState(authModal.mode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authModal.open) {
      setMode(authModal.mode);
      setError("");
      stopLenis();
    } else {
      startLenis();
    }
  }, [authModal]);

  useEffect(() => {
    if (!authModal.open) return;
    const onKey = (e) => e.key === "Escape" && closeAuth();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [authModal.open, closeAuth]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u =
        mode === "register"
          ? await registerWithPassword(form.name, form.email, form.password)
          : await signInWithPassword(form.email, form.password);
      toast.success(mode === "register" ? "Welcome to Adveshta!" : "Signed in", {
        description: u?.name || u?.email,
      });
      setForm({ name: "", email: "", password: "" });
      closeAuth();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const isRegister = mode === "register";

  return (
    <AnimatePresence>
      {authModal.open && (
        <motion.div
          data-testid="auth-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm"
          onClick={closeAuth}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-modal-title"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl bg-cream border border-sand p-8 md:p-10 shadow-[0_40px_80px_-30px_rgba(11,38,30,0.6)] max-h-[92vh] overflow-y-auto"
          >
            <button
              data-testid="auth-modal-close"
              onClick={closeAuth}
              aria-label="Close"
              className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-sand text-sage hover:text-pine hover:border-pine transition-colors duration-300"
            >
              <X className="h-4 w-4" />
            </button>

            <LogoMark className="h-10 w-10" />
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              {isRegister ? "Create account" : "Welcome back"}
            </p>
            <h2 id="auth-modal-title" className="mt-3 font-serif text-3xl text-pine leading-tight">
              {isRegister ? "Join the Adveshta family." : "Sign in to Adveshta."}
            </h2>
            <p className="mt-3 text-sm text-sage leading-relaxed">
              {isRegister
                ? "Track your enquiries and farm-visit bookings in one place."
                : "See your enquiries and farm-visit bookings."}
            </p>

            <button
              data-testid="auth-google-button"
              type="button"
              onClick={signInWithGoogle}
              className="mt-7 w-full inline-flex items-center justify-center gap-3 rounded-full border border-pine/30 bg-white px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-pine hover:border-pine hover:bg-pine hover:text-cream transition-colors duration-300"
            >
              <GoogleIcon /> Continue with Google
            </button>

            <div className="my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-sand" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">or with email</span>
              <span className="h-px flex-1 bg-sand" />
            </div>

            <form data-testid="auth-form" onSubmit={submit} className="grid gap-4">
              {isRegister && (
                <Field icon={User} label="Full name">
                  <input
                    data-testid="auth-input-name"
                    className={inputCls}
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    autoComplete="name"
                  />
                </Field>
              )}
              <Field icon={Mail} label="Email">
                <input
                  data-testid="auth-input-email"
                  type="email"
                  className={inputCls}
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </Field>
              <Field icon={Lock} label="Password">
                <input
                  data-testid="auth-input-password"
                  type={showPw ? "text" : "password"}
                  className={`${inputCls} pr-12`}
                  required
                  minLength={isRegister ? 8 : 1}
                  value={form.password}
                  onChange={set("password")}
                  placeholder={isRegister ? "At least 8 characters" : "Your password"}
                  autoComplete={isRegister ? "new-password" : "current-password"}
                />
                <button
                  type="button"
                  data-testid="auth-toggle-password"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-sage hover:text-pine"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </Field>

              {error && (
                <p
                  data-testid="auth-error"
                  role="alert"
                  className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {error}
                </p>
              )}

              <button
                data-testid="auth-submit-button"
                type="submit"
                disabled={busy}
                className="mt-1 w-full rounded-full bg-pine text-cream px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-royal transition-colors duration-300 disabled:opacity-60"
              >
                {busy ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-sage">
              {isRegister ? "Already have an account?" : "New to Adveshta?"}{" "}
              <button
                type="button"
                data-testid="auth-switch-mode"
                onClick={() => {
                  setMode(isRegister ? "login" : "register");
                  setError("");
                }}
                className="text-pine font-medium underline decoration-gold/60 underline-offset-4 hover:text-royal"
              >
                {isRegister ? "Sign in" : "Create an account"}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
